import Box from '@amzn/awsui-components-react/polaris/box';
import Button from '@amzn/awsui-components-react/polaris/button';
import ColumnLayout from '@amzn/awsui-components-react/polaris/column-layout';
import Container from '@amzn/awsui-components-react/polaris/container';
import Grid from '@amzn/awsui-components-react/polaris/grid';
import Header from '@amzn/awsui-components-react/polaris/header';
import Icon from '@amzn/awsui-components-react/polaris/icon';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import { LandingPageOverview } from '@amzn/event-engine-js-utils';
import { useIsDarkMode } from '@amzn/event-engine-js-utils';
import useNotification from 'hooks/use-notification';
import React, { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import * as Yup from 'yup';

import { routes } from '../../constants';
import LandingPageConfig from './LandingPage.config';
import styles from './LandingPage.module.scss';

interface LandingPageProps {
    target?: HTMLElement | undefined;
}

const getStyleByKey = (key: string): string => styles[key];

const [
    headerStyle,
    headerTitleStyle,
    headerSubTitleStyle,
    mainContentAreaStyle,
    bannerStyle,
    darkBanner,
    lightBanner,
    sidebarStyle,
    listSeparatorStyle,
    listItemStyle,
    ctaButton,
    learningCatalogImageContainer,
    learningCatalogImage,
] = [
    getStyleByKey('custom-home__header'),
    getStyleByKey('custom-home__header-title'),
    getStyleByKey('custom-home__header-sub-title'),
    getStyleByKey('custom-home-main-content-area'),
    getStyleByKey('custom-home__banner'),
    getStyleByKey('darkBanner'),
    getStyleByKey('lightBanner'),
    getStyleByKey('custom-home__sidebar'),
    getStyleByKey('custom-list-separator'),
    getStyleByKey('custom-list-separator__item'),
    getStyleByKey('cta-button'),
    getStyleByKey('learning-catalog-container__item'),
    getStyleByKey('learning-catalog__image'),
];

const FeaturesListComponent = ({
    title,
    description,
}: {
    title: string;
    description: string;
}) => {
    return (
        <div>
            <Box variant="h3" padding={{ top: 'n' }}>
                {title}
            </Box>
            <Box variant="p">{description}</Box>
        </div>
    );
};

const FormSchema = Yup.object({
    accessCode: Yup.string().required('Required'),
});

export type FormSchemaType = Yup.InferType<typeof FormSchema>;

const CallToActionComponent = () => {
    const history = useHistory();
    const { clearAllNotifications } = useNotification();

    const onGetStartedClick = useCallback(() => {
        clearAllNotifications();
        history.push(routes.joinEvent);
    }, [history]);

    return (
        <Container>
            <SpaceBetween size="xl">
                <Box variant="h2" padding="n">
                    {config.callToAction.title}
                </Box>
                <SpaceBetween direction="vertical" size="l">
                    <Box variant="p">{config.callToAction.subText}</Box>
                    <Button
                        className={ctaButton}
                        variant="primary"
                        onClick={onGetStartedClick}>
                        {config.callToAction.buttonText}
                    </Button>
                </SpaceBetween>
            </SpaceBetween>
        </Container>
    );
};

const { config } = LandingPageConfig;

const commonLandingPageOverviewProps = {
    enableNumbering: true,
    gridContainer: {
        isWebConsoleLandingPageOverview: false,
        gridDefinition: [{ colspan: { default: 12, xxs: 12 } }],
    },
    gridItemContainer: {
        gridDefinition: [
            {
                colspan: { default: 4, s: 3 },
            },
            {
                colspan: { default: 4, s: 3 },
            },
            {
                colspan: { default: 4, s: 3 },
            },
            {
                colspan: { default: 4, s: 3 },
            },
        ],
    },
};

const LandingPage: React.FC<LandingPageProps> = ({
    target,
}: LandingPageProps) => {
    const isDarkMode = useIsDarkMode(target);
    const bannerStyles = [bannerStyle, isDarkMode ? darkBanner : lightBanner];

    return (
        <>
            <Box
                data-testid={`banner-dark-mode-${!!isDarkMode}`}
                margin={{ bottom: 'l' }}>
                <div className={headerStyle}>
                    <Box padding={{ vertical: 'xxl', horizontal: 's' }}>
                        <Grid
                            gridDefinition={[
                                {
                                    colspan: { xl: 6, l: 5, s: 6, xxs: 10 },
                                    offset: { l: 2, xxs: 1 },
                                },

                                {
                                    colspan: { xl: 2, l: 3, s: 4, xxs: 10 },
                                    offset: { s: 0, xxs: 1 },
                                },
                            ]}>
                            <div className={headerTitleStyle}>
                                <Box
                                    variant="h1"
                                    fontWeight="bold"
                                    padding="n"
                                    fontSize="display-l"
                                    color="inherit">
                                    {config.header.title}
                                </Box>
                                <Box
                                    fontWeight="light"
                                    padding={{ bottom: 's' }}
                                    fontSize="display-l"
                                    color="inherit">
                                    {config.header.description}
                                </Box>
                                <Box variant="p" fontWeight="light">
                                    <span className={headerSubTitleStyle}>
                                        {config.header.subText}
                                    </span>
                                </Box>
                            </div>
                            <Box margin={{ bottom: 'xxl' }}>
                                <CallToActionComponent />
                            </Box>
                        </Grid>
                    </Box>
                </div>

                <Box
                    margin={{ top: 's' }}
                    padding={{ top: 's', horizontal: 's' }}>
                    <Grid
                        gridDefinition={[
                            {
                                colspan: { xl: 6, l: 5, s: 6, xxs: 10 },
                                offset: { l: 2, xxs: 1 },
                            },
                            {
                                colspan: { xl: 2, l: 3, s: 4, xxs: 10 },
                                offset: { s: 0, xxs: 1 },
                            },
                        ]}>
                        <div className={mainContentAreaStyle}>
                            <SpaceBetween size="l">
                                <div>
                                    <Box
                                        fontSize="heading-xl"
                                        fontWeight="normal"
                                        padding={{ bottom: 'm' }}
                                        variant="h2">
                                        {config.bannerConfig.title}
                                    </Box>
                                    <Container>
                                        <div className={bannerStyles.join(' ')}>
                                            <SpaceBetween size="l">
                                                <Box
                                                    fontSize="heading-xl"
                                                    fontWeight="normal"
                                                    variant="h2">
                                                    {
                                                        config.bannerConfig
                                                            .firstRow.title
                                                    }
                                                </Box>
                                                <LandingPageOverview
                                                    {...commonLandingPageOverviewProps}
                                                    config={
                                                        config.bannerConfig
                                                            .firstRow.config
                                                    }
                                                />
                                                <Box
                                                    fontSize="heading-xl"
                                                    fontWeight="normal"
                                                    variant="h2">
                                                    {
                                                        config.bannerConfig
                                                            .secondRow.title
                                                    }
                                                </Box>
                                                <LandingPageOverview
                                                    {...commonLandingPageOverviewProps}
                                                    config={
                                                        config.bannerConfig
                                                            .secondRow.config
                                                    }
                                                />
                                            </SpaceBetween>
                                        </div>
                                    </Container>
                                </div>

                                <div>
                                    <Box
                                        variant="h1"
                                        tagOverride="h2"
                                        padding={{ bottom: 's', top: 'n' }}>
                                        {config.benefitsAndFeatures.title}
                                    </Box>
                                    <Container>
                                        <ColumnLayout
                                            columns={2}
                                            variant="text-grid">
                                            {config.benefitsAndFeatures.features.map(
                                                (feature) => (
                                                    <FeaturesListComponent
                                                        key={feature.id}
                                                        {...feature}
                                                    />
                                                )
                                            )}
                                        </ColumnLayout>
                                    </Container>
                                </div>
                            </SpaceBetween>
                        </div>
                        <div className={sidebarStyle}>
                            <SpaceBetween size="xxl">
                                <Container
                                    header={
                                        <Header variant="h2">
                                            {config.learningCatalog.title}
                                        </Header>
                                    }>
                                    <SpaceBetween size="m">
                                        <div
                                            className={
                                                learningCatalogImageContainer
                                            }>
                                            <img
                                                className={learningCatalogImage}
                                                alt={
                                                    config.learningCatalog
                                                        .iconAltText
                                                }
                                                src={
                                                    isDarkMode
                                                        ? config.learningCatalog
                                                              .darkIcon
                                                        : config.learningCatalog
                                                              .lightIcon
                                                }
                                            />
                                        </div>
                                        <Button className={ctaButton} disabled>
                                            {config.learningCatalog.buttonText}
                                        </Button>
                                    </SpaceBetween>
                                </Container>

                                <Container
                                    header={
                                        <Header variant="h2">
                                            {config.whatCanYouDoHere.title}
                                        </Header>
                                    }>
                                    <ul
                                        aria-label={
                                            config.whatCanYouDoHere.title
                                        }
                                        className={listSeparatorStyle}>
                                        <SpaceBetween size="m">
                                            {config.whatCanYouDoHere.list.map(
                                                (l) => {
                                                    return (
                                                        <li
                                                            key={l.detail}
                                                            className={
                                                                listItemStyle
                                                            }>
                                                            <Icon
                                                                name="status-positive"
                                                                size="small"
                                                                variant="success"
                                                            />
                                                            <Box variant="p">
                                                                {l.detail}
                                                            </Box>
                                                        </li>
                                                    );
                                                }
                                            )}
                                        </SpaceBetween>
                                    </ul>
                                </Container>
                            </SpaceBetween>
                        </div>
                    </Grid>
                </Box>
            </Box>
        </>
    );
};

export default LandingPage;
